import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Edit, Trash2, Table2, Clock } from 'lucide-react';
import { api } from '@/lib/api';
import type { VenueArea } from '@/types';

export default function AreasList() {
  const { venueId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const bookingTypeId = searchParams.get('type');

  const { data: venue } = useQuery({
    queryKey: ['venue', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}`);
      return response.data;
    },
  });

  const { data: areas, isLoading } = useQuery<VenueArea[]>({
    queryKey: ['venue-areas', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas`);
      return response.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (areaId: string) => {
      await api.delete(`/admin/venues/${venueId}/areas/${areaId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-areas', venueId] });
    },
  });

  // Filter areas by booking type if specified
  const filteredAreas = bookingTypeId
    ? areas?.filter(a => a.booking_type_id === bookingTypeId)
    : areas;

  const bookingTypeName = filteredAreas?.[0]?.booking_type_name || 'All Types';

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/booking-config`)}
          className="p-2 hover:bg-gray-100 rounded-md"
          title="Back to Booking Configuration"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Booking Areas</h1>
          <p className="text-sm text-gray-500">
            {(venue as Record<string, unknown>)?.name as string} - {bookingTypeName}
          </p>
        </div>
        <Link
          to={`/venues/${venueId}/booking-config/areas/new${bookingTypeId ? `?type=${bookingTypeId}` : ''}`}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Area
        </Link>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Area Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tables
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sessions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-12"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                    <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  </tr>
                ))
              ) : filteredAreas?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No areas configured yet. Click "Add Area" to create one.
                  </td>
                </tr>
              ) : (
                filteredAreas?.map((area) => (
                  <tr key={area.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{area.name}</p>
                        {area.description && (
                          <p className="text-sm text-gray-500">{area.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {area.booking_type_name}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {area.table_count || 0} tables
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {area.session_count || 0} sessions
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        area.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {area.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Link
                          to={`/venues/${venueId}/booking-config/areas/${area.id}/edit`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Edit Area"
                        >
                          <Edit className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/venues/${venueId}/booking-config/areas/${area.id}/tables`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Configure Tables"
                        >
                          <Table2 className="h-4 w-4" />
                        </Link>
                        <Link
                          to={`/venues/${venueId}/booking-config/areas/${area.id}/sessions`}
                          className="p-2 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                          title="Configure Sessions"
                        >
                          <Clock className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this area?')) {
                              deleteMutation.mutate(area.id);
                            }
                          }}
                          className="p-2 rounded-md hover:bg-gray-100 text-red-500 hover:text-red-700"
                          title="Delete Area"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
